/** Layout — 微信公众号后台「数据分析」风
 *
 * 设计:
 * - 顶栏 48px sticky 白底,左 Logo + 公众号名,中 6 个主导航,右 抓取状态 + 用户菜单
 * - 主导航 active = 微信绿底色 2px 底线
 * - Content max-w-1280 居中,body 灰底,卡片白底 1px 边框
 * - 去掉"健康度 82"自定义品牌指标(WeChat 后台不放这个)
 */
import { Layout as AntdLayout, Breadcrumb, Dropdown } from 'antd'
import { Outlet, useLocation, useNavigate, Link } from 'react-router-dom'
import {
  Home,
  FileText,
  Users,
  MessageSquare,
  FileBarChart,
  MessageCircleMore,
  Settings,
  ChevronDown,
  LogOut,
  ScrollText,
  BarChart3,
} from 'lucide-react'
import ScrapeStatus from './ScrapeStatus'
import { clearAuth, getUser } from '@/lib/auth'

const { Header, Content } = AntdLayout

interface Crumb {
  label: string
  to?: string
}

function useBreadcrumbs(): Crumb[] {
  const { pathname } = useLocation()
  const segs = pathname.split('/').filter(Boolean)
  if (segs.length === 0) return [{ label: '今日概览' }]
  const crumbs: Crumb[] = [{ label: '首页', to: '/' }]
  let path = ''
  for (const seg of segs) {
    path += `/${seg}`
    if (seg === 'articles' && segs.length === 1) crumbs.push({ label: '图文分析', to: path })
    else if (seg.startsWith('articles')) crumbs.push({ label: '单篇分析' })
    else if (seg === 'users') crumbs.push({ label: '用户分析', to: path })
    else if (seg === 'comments') crumbs.push({ label: '留言管理', to: path })
    else if (seg === 'messages') crumbs.push({ label: '消息分析', to: path })
    else if (seg === 'reports') crumbs.push({ label: '数据报告', to: path })
    else if (seg === 'crawl-logs') crumbs.push({ label: '抓取日志', to: path })
    else if (seg === 'wechat-api') crumbs.push({ label: 'API 数据', to: path })
    else if (seg === 'all-wechat-apis') crumbs.push({ label: '所有公众号数据', to: path })
    else if (seg === 'settings') crumbs.push({ label: '设置', to: path })
  }
  return crumbs
}

export default function Layout() {
  const crumbs = useBreadcrumbs()
  const navigate = useNavigate()
  const location = useLocation()
  const user = getUser()

  // 导航项 — viewer 角色只能看到多账号概览
  const allNavItems = [
    { key: '/',         icon: <Home size={15} />,            label: '今日概览' },
    { key: '/articles', icon: <FileText size={15} />,        label: '图文分析' },
    { key: '/users',    icon: <Users size={15} />,           label: '用户分析' },
    { key: '/messages', icon: <MessageCircleMore size={15} />,label: '消息分析' },
    { key: '/comments', icon: <MessageSquare size={15} />,   label: '留言管理' },
    { key: '/wechat-api', icon: <BarChart3 size={15} />,     label: 'API 数据' },
    { key: '/all-wechat-apis', icon: <BarChart3 size={15} />, label: '所有公众号' },
    { key: '/wechat-config', icon: <Settings size={15} />,   label: 'AppID 配置' },
    { key: '/reports',  icon: <FileBarChart size={15} />,     label: '数据报告' },
    { key: '/crawl-logs', icon: <ScrollText size={15} />,    label: '抓取日志' },
  ]

  const navItems = user?.role === 'viewer'
    ? allNavItems.filter(item => item.key === '/all-wechat-apis')
    : allNavItems

  function logout() {
    clearAuth()
    navigate('/login', { replace: true })
  }

  return (
    <AntdLayout className="min-h-screen">
      <Header
        className="!px-6 !h-12 border-b border-ink-200 flex items-center gap-6 sticky top-0 z-10 bg-white"
        style={{ lineHeight: 'normal', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
      >
        {/* Logo + 公众号名 */}
        <Link to="/" className="flex items-center gap-2 no-underline shrink-0 group">
          <div className="w-7 h-7 rounded bg-primary-500 flex items-center justify-center text-white font-semibold text-sm transition-transform group-hover:scale-110">
            f
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-semibold text-ink-900 text-sm transition-colors group-hover:text-primary-600">fenxi</span>
            <span className="text-[10px] text-ink-500 mt-0.5">公众号数据分析</span>
          </div>
        </Link>

        {/* 主导航(微信风:active 底色 + 2px 微信绿底线) */}
        <nav className="flex items-center gap-0.5 flex-1 ml-2">
          {navItems.map((item) => {
            const active =
              location.pathname === item.key ||
              (item.key !== '/' && location.pathname.startsWith(item.key))
            return (
              <Link
                key={item.key}
                to={item.key}
                className={`flex items-center gap-1.5 px-3 h-12 text-[13px] no-underline transition-all relative ${
                  active
                    ? 'text-primary-600 font-medium'
                    : 'text-ink-700 hover:text-ink-900 hover:bg-ink-50'
                }`}
              >
                {item.icon}
                {item.label}
                {active && (
                  <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-primary-500 rounded-full" />
                )}
              </Link>
            )
          })}
        </nav>

        {/* 抓取状态 + 设置 + 用户菜单 */}
        <div className="flex items-center gap-2 shrink-0">
          <ScrapeStatus />
          <Link
            to="/settings"
            className="text-ink-500 hover:text-primary-600 p-1.5 rounded hover:bg-ink-50 transition-colors"
            title="设置"
          >
            <Settings size={16} />
          </Link>
          <Dropdown
            placement="bottomRight"
            menu={{
              items: [
                {
                  key: 'username',
                  disabled: true,
                  label: (
                    <div className="text-xs text-ink-500 px-1">
                      已登录:<span className="text-ink-900 font-medium ml-1">{user?.username || '—'}</span>
                    </div>
                  ),
                },
                { type: 'divider' },
                {
                  key: 'logout',
                  icon: <LogOut size={13} />,
                  label: '退出登录',
                  onClick: logout,
                },
              ],
            }}
          >
            <button
              type="button"
              className="flex items-center gap-1 px-2 h-8 text-[13px] text-ink-700 hover:text-ink-900 hover:bg-ink-50 rounded transition-all"
            >
              <span className="hidden sm:inline">{user?.username || '账号'}</span>
              <ChevronDown size={12} className="text-ink-500 transition-transform group-hover:rotate-180" />
            </button>
          </Dropdown>
        </div>
      </Header>

      <Content className="px-6 py-5 max-w-[1280px] mx-auto w-full">
        {/* 面包屑(微信风:小字浅色) */}
        <div className="mb-3">
          <Breadcrumb
            items={crumbs.map((c) => ({
              title: c.to ? <Link to={c.to}>{c.label}</Link> : c.label,
            }))}
          />
        </div>

        {/* 页面内容 */}
        <Outlet />
      </Content>
    </AntdLayout>
  )
}
